import * as constants from "../constants/CONSTANT";

const SchemaApi = {
  // Create new object (table + object config)
  async createObject(objectName, objectLabel, pluralLabel, nameFieldLabel = 'Name', nameFieldType = 'text', nameStartNumber = 1, namePrefix = '', nameFormatMask = '0000', nameFieldUnique = false, allowActivity = false, allowEmailLog = false, allowNotes = false, createTab = false, isStandard = false) {
    console.log("Creating new object:", { objectName, objectLabel, pluralLabel, nameFieldLabel, nameFieldType, nameStartNumber, namePrefix, nameFormatMask, nameFieldUnique, allowActivity, allowEmailLog, allowNotes, createTab, isStandard });
    const token = localStorage.getItem("token");

    let response = await fetch(constants.API_BASE_URL + "/api/schema/object", {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({
        objectName,
        objectLabel,
        pluralLabel,
        nameFieldLabel,
        nameFieldType,
        nameStartNumber,
        namePrefix,
        nameFormatMask,
        name_field_unique: nameFieldUnique,
        allow_activity: allowActivity,
        allow_email_log: allowEmailLog,
        allow_notes: allowNotes,
        create_tab: createTab,
        is_standard: isStandard
      }),
    });
    return await response.json();
  },

  // Update object config (label, active status, etc.)
  async updateObjectConfig(configId, updatePayload) {
    console.log("Updating Object Config:", { configId, updatePayload });
    const token = localStorage.getItem("token");

    let response = await fetch(
      constants.API_BASE_URL + `/api/objectconfig/${configId}`,
      {
        method: "PUT",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(updatePayload),
      }
    );

    return await response.json();
  },

  // Get all tables in schema
  async getAllTables() {
    console.log("Fetching all tables");
    const token = localStorage.getItem("token");

    let response = await fetch(constants.API_BASE_URL + "/api/schema/tables", {
      method: "GET",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    });

    return await response.json();
  },

  // Get table information
  async getTableInfo(tableName) {
    console.log("Fetching table info:", tableName);
    const token = localStorage.getItem("token");

    let response = await fetch(
      constants.API_BASE_URL + `/api/schema/table/${tableName}`,
      {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      }
    );

    return await response.json();
  },

  // Check if table exists
  async tableExists(tableName) {
    console.log("Checking if table exists:", tableName);
    const token = localStorage.getItem("token");

    let response = await fetch(
      constants.API_BASE_URL + `/api/schema/table/${tableName}/exists`,
      {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      }
    );

    return await response.json();
  },

  // Add field to existing table
  async addField(tableName, fieldData) {
    console.log("Adding field to table:", { tableName, fieldData });
    const token = localStorage.getItem("token");

    let response = await fetch(constants.API_BASE_URL + "/api/schema/field", {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({
        tableName,
        field: fieldData
      }),
    });

    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        message: body.message || `Request failed (${response.status})`,
        code: body.code,
        httpStatus: response.status,
        ...body,
      };
    }
    return body;
  },

  // Update object config field (grouped under schema ops for convenience)
  async updateField(objectName, fieldId, updatePayload) {
    if (!objectName) {
      throw new Error("objectName is required to update a field");
    }
    const token = localStorage.getItem("token");
    let response = await fetch(
      constants.API_BASE_URL + `/api/schema/field/${encodeURIComponent(objectName)}/${fieldId}`,
      {
        method: "PUT",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(updatePayload),
      }
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        message: body.message || `Request failed (${response.status})`,
        code: body.code,
        httpStatus: response.status,
        ...body,
      };
    }
    return body;
  },

  // Check if field exists
  async fieldExists(tableName, fieldName) {
    console.log("Checking if field exists:", { tableName, fieldName });
    const token = localStorage.getItem("token");

    let response = await fetch(
      constants.API_BASE_URL + `/api/schema/field/${tableName}/${fieldName}`,
      {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      }
    );

    return await response.json();
  },

  // Get supported data types
  async getDataTypes() {
    console.log("Fetching supported data types");
    const token = localStorage.getItem("token");

    let response = await fetch(constants.API_BASE_URL + "/api/schema/data-types", {
      method: "GET",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    });

    return await response.json();
  },

  // Drop table
  async dropTable(tableName) {
    console.log("Dropping table:", tableName);
    const token = localStorage.getItem("token");

    let response = await fetch(
      constants.API_BASE_URL + `/api/schema/table/${tableName}`,
      {
        method: "DELETE",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      }
    );

    return await response.json();
  },

  // Drop field from table
  async dropField(tableName, fieldName) {
    console.log("Dropping field:", { tableName, fieldName });
    const token = localStorage.getItem("token");

    let response = await fetch(
      constants.API_BASE_URL + `/api/schema/field/${tableName}/${fieldName}`,
      {
        method: "DELETE",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      }
    );

    return await response.json();
  },

  // Future methods can be added here:
  // - createIndex(tableName, indexData)
  // - dropIndex(tableName, indexName)
  // - createConstraint(tableName, constraintData)
  // - dropConstraint(tableName, constraintName)
  // - backupTable(tableName)
  // - restoreTable(tableName, backupData)
  // - migrateTable(tableName, migrationData)
  // - validateSchema()
  // - getSchemaVersion()
  // - updateSchemaVersion(version)
};

export default SchemaApi;
